import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '@tremor/react';
import { Search, Calendar, ChevronLeft, ChevronRight, Filter, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { dashboardService, DailyScheduledItem, TrackingStatusFilters } from '../services/dashboard.service';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
} from '@tanstack/react-table';

const columnHelper = createColumnHelper<DailyScheduledItem>();

interface TrackingExecutionListProps {
  externalFilter?: string | null;
  onFilterApplied?: () => void;
}

export default function TrackingExecutionList({ externalFilter, onFilterApplied }: TrackingExecutionListProps = {}) {
  const navigate = useNavigate();
  const [urlParams, setUrlParams] = useSearchParams();
  const tableRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<DailyScheduledItem[]>([]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0
  });

  // Filters
  const [date, setDate] = useState(''); // Empty = all dates
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [gatewayFilter, setGatewayFilter] = useState('');
  const [objectStatusFilter, setObjectStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [pageSize, setPageSize] = useState(10);

  // Dynamic filter options
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);
  const [availableGateways, setAvailableGateways] = useState<string[]>([]);

  // Process URL filter on mount
  useEffect(() => {
    const filterParam = urlParams.get('filter');
    if (filterParam) {
      // Scroll to table
      setTimeout(() => {
        tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);

      // Apply filter
      switch (filterParam) {
        case 'delivered':
          setObjectStatusFilter('delivered');
          setShowFilters(true);
          break;
        case 'active':
          setObjectStatusFilter('active');
          setShowFilters(true);
          break;
        case 'inActivity':
          setObjectStatusFilter('active');
          setStatusFilter('notError');
          setShowFilters(true);
          break;
        case 'activeErrors':
          setObjectStatusFilter('active');
          setStatusFilter('error');
          setShowFilters(true);
          break;
      }

      // Clear URL param after applying
      setUrlParams({});
    }
  }, [urlParams, setUrlParams]);

  // Process external filter from Dashboard
  useEffect(() => {
    if (externalFilter) {
      // Apply filter
      switch (externalFilter) {
        case 'delivered':
          setObjectStatusFilter('delivered');
          setStatusFilter('');
          setShowFilters(true);
          break;
        case 'active':
          setObjectStatusFilter('active');
          setStatusFilter('');
          setShowFilters(true);
          break;
        case 'inActivity':
          setObjectStatusFilter('active');
          setStatusFilter('notError');
          setShowFilters(true);
          break;
        case 'activeErrors':
          setObjectStatusFilter('active');
          setStatusFilter('error');
          setShowFilters(true);
          break;
      }

      // Notify parent that filter was applied
      if (onFilterApplied) {
        onFilterApplied();
      }
    }
  }, [externalFilter, onFilterApplied]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Get current sorting state
      const currentSort = sorting[0];

      const filters: TrackingStatusFilters & { sortBy?: string; sortOrder?: string; objectStatus?: string } = {
        page: pagination.page,
        limit: pageSize,
        ...(date && { date }),
        ...(search && { search }),
        ...(statusFilter && { status: statusFilter }),
        ...(gatewayFilter && { gateway: gatewayFilter }),
        ...(objectStatusFilter && { objectStatus: objectStatusFilter }),
        ...(currentSort && {
          sortBy: currentSort.id,
          sortOrder: currentSort.desc ? 'desc' : 'asc'
        })
      };

      const response = await dashboardService.getRecentTrackingStatus(filters);
      setItems(response.items);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Error fetching tracking status:', error);
    } finally {
      setLoading(false);
    }
  }, [date, pagination.page, search, statusFilter, gatewayFilter, objectStatusFilter, pageSize, sorting]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [pageSize]);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const filters = await dashboardService.getTrackingFilters();
        setAvailableStatuses(filters.statuses);
        setAvailableGateways(filters.gateways);
      } catch (error) {
        console.error('Error fetching filters:', error);
      }
    };
    fetchFilters();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchData();
  };

  const handleTrackingClick = (trackingId: string, gateway: string) => {
    navigate(`/tracking?gateway=${gateway}&trackId=${trackingId}`);
  };

  const getStatusLabel = useCallback((status: string): string => {
    const statusLabels: Record<string, string> = {
      'new': 'Novo',
      'scheduled': 'Agendado',
      'processing': 'Processando',
      'processed': 'Processado',
      'success': 'Sucesso',
      'delivered': 'Entregue',
      'intransit': 'Em Trânsito',
      'posted': 'Postado',
      'out_for_delivery': 'Saiu para Entrega',
      'failed_attempt': 'Tentativa Falhou',
      'exception': 'Exceção',
      'error': 'Erro',
      'timeout': 'Timeout',
      'awaiting_pickup': 'Aguardando Coleta',
      'in_transit': 'Em Trânsito',
    };

    return statusLabels[status] || status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }, []);

  const renderStatusBadge = useCallback((status: string) => {
    const statusConfig: Record<string, string> = {
      'new': 'bg-gray-100 text-gray-800',
      'scheduled': 'bg-gray-100 text-gray-800',
      'processing': 'bg-blue-100 text-blue-800',
      'processed': 'bg-green-100 text-green-800',
      'success': 'bg-green-100 text-green-800',
      'delivered': 'bg-green-100 text-green-800',
      'intransit': 'bg-yellow-100 text-yellow-800',
      'posted': 'bg-blue-100 text-blue-800',
      'out_for_delivery': 'bg-purple-100 text-purple-800',
      'failed_attempt': 'bg-orange-100 text-orange-800',
      'exception': 'bg-red-100 text-red-800',
      'error': 'bg-red-100 text-red-800',
      'timeout': 'bg-red-100 text-red-800',
      'awaiting_pickup': 'bg-gray-100 text-gray-800',
      'in_transit': 'bg-yellow-100 text-yellow-800',
    };

    const colorClass = statusConfig[status] || 'bg-gray-100 text-gray-800';

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
        {getStatusLabel(status)}
      </span>
    );
  }, [getStatusLabel]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('trackingId', {
        header: ({ column }) => (
          <button
            className="flex items-center gap-1 hover:text-blue-600"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Tracking ID
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className="h-4 w-4" />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className="h-4 w-4" />
            ) : (
              <ArrowUpDown className="h-4 w-4 opacity-50" />
            )}
          </button>
        ),
        cell: info => (
          <span className="font-medium text-gray-900">{info.getValue()}</span>
        ),
      }),
      columnHelper.accessor('gateway', {
        header: ({ column }) => (
          <button
            className="flex items-center gap-1 hover:text-blue-600"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Transportadora
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className="h-4 w-4" />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className="h-4 w-4" />
            ) : (
              <ArrowUpDown className="h-4 w-4 opacity-50" />
            )}
          </button>
        ),
        cell: info => (
          <span className="capitalize">{info.getValue()}</span>
        ),
      }),
      columnHelper.accessor('scheduledAt', {
        header: ({ column }) => (
          <button
            className="flex items-center gap-1 hover:text-blue-600"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Data de Execução
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className="h-4 w-4" />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className="h-4 w-4" />
            ) : (
              <ArrowUpDown className="h-4 w-4 opacity-50" />
            )}
          </button>
        ),
        cell: info => formatDate(info.getValue()),
      }),
      columnHelper.accessor('status', {
        header: ({ column }) => (
          <button
            className="flex items-center gap-1 hover:text-blue-600"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Status
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className="h-4 w-4" />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className="h-4 w-4" />
            ) : (
              <ArrowUpDown className="h-4 w-4 opacity-50" />
            )}
          </button>
        ),
        cell: info => (
          <div className="flex items-center gap-2">
            {renderStatusBadge(info.getValue())}
            {info.row.original.errorMessage && (
              <span className="text-xs text-gray-600" title={info.row.original.errorMessage}>
                ({info.row.original.errorMessage.length > 30
                  ? info.row.original.errorMessage.substring(0, 30) + '...'
                  : info.row.original.errorMessage})
              </span>
            )}
          </div>
        ),
      }),
      columnHelper.accessor('objectStatus', {
        header: ({ column }) => (
          <button
            className="flex items-center gap-1 hover:text-blue-600"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Status do Objeto
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className="h-4 w-4" />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className="h-4 w-4" />
            ) : (
              <ArrowUpDown className="h-4 w-4 opacity-50" />
            )}
          </button>
        ),
        cell: info => {
          const value = info.getValue();
          const statusConfig: Record<string, string> = {
            'active': 'bg-blue-100 text-blue-800',
            'delivered': 'bg-green-100 text-green-800',
            'cancelled': 'bg-gray-100 text-gray-800',
          };
          const label = value === 'active' ? 'Ativo' : value === 'delivered' ? 'Entregue' : value === 'cancelled' ? 'Cancelado' : value || '-';
          const colorClass = value ? statusConfig[value] || 'bg-gray-100 text-gray-800' : 'bg-gray-100 text-gray-800';
          
          return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
              {label}
            </span>
          );
        },
      }),
      columnHelper.accessor('trackedAt', {
        header: ({ column }) => (
          <button
            className="flex items-center gap-1 hover:text-blue-600"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          >
            Última Verificação
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className="h-4 w-4" />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className="h-4 w-4" />
            ) : (
              <ArrowUpDown className="h-4 w-4 opacity-50" />
            )}
          </button>
        ),
        cell: info => formatDate(info.getValue()),
        sortingFn: 'datetime',
      }),
    ],
    [renderStatusBadge]
  );

  const table = useReactTable({
    data: items,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount: pagination.totalPages,
  });

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('pt-BR');
  };

  return (
    <Card>
      <div className="mb-6" ref={tableRef}>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Lista de Execução de Rastreamentos</h3>

        {/* Search and Date Filter Bar */}
        <div className="flex flex-col gap-4 mb-4">
          <div className="flex gap-4">
            <form onSubmit={handleSearch} className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Buscar por Tracking ID..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </form>

            <div className="flex gap-2">
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Todas as datas"
                />
              </div>

              <button
                onClick={() => setDate(new Date().toISOString().split('T')[0])}
                className="px-4 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
              >
                Hoje
              </button>

              <button
                onClick={() => setDate('')}
                className={`px-4 py-2 text-sm rounded-lg ${
                  date ? 'bg-gray-100 hover:bg-gray-200' : 'bg-gray-200 cursor-not-allowed'
                }`}
                disabled={!date}
              >
                Todos
              </button>
            </div>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Filter size={20} />
              Filtros
            </button>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="flex gap-4 p-4 bg-gray-50 rounded-lg">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos os Status</option>
                <option value="error">Erros</option>
                <option value="notError">Sem Erros</option>
                {availableStatuses.map(status => (
                  <option key={status} value={status}>
                    {getStatusLabel(status)}
                  </option>
                ))}
              </select>

              <select
                value={gatewayFilter}
                onChange={(e) => setGatewayFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todas as Transportadoras</option>
                {availableGateways.map(gw => (
                  <option key={gw} value={gw}>
                    {gw.charAt(0).toUpperCase() + gw.slice(1)}
                  </option>
                ))}
              </select>

              <select
                value={objectStatusFilter}
                onChange={(e) => setObjectStatusFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Todos os Status de Objeto</option>
                <option value="active">Ativo</option>
                <option value="delivered">Entregue</option>
                <option value="cancelled">Cancelado</option>
              </select>

              <button
                onClick={() => {
                  setStatusFilter('');
                  setGatewayFilter('');
                  setObjectStatusFilter('');
                  setSearch('');
                }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
              >
                Limpar Filtros
              </button>
            </div>
          )}
        </div>

        {/* Results Summary and Page Size Selector */}
        <div className="flex justify-between items-center mb-4">
          <div className="text-sm text-gray-600">
            Mostrando {items.length} de {pagination.total} rastreamentos
            {date && ` para ${new Date(date).toLocaleDateString('pt-BR')}`}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Itens por página:</label>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="px-3 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={200}>200</option>
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <th
                    key={header.id}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center">
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                  </div>
                </td>
              </tr>
            ) : table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map(row => (
                <tr
                  key={row.id}
                  className="hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => handleTrackingClick(
                    row.original.trackingId,
                    row.original.gateway
                  )}
                >
                  {row.getVisibleCells().map(cell => (
                    <td
                      key={cell.id}
                      className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500">
                  Nenhum rastreamento encontrado para os filtros selecionados
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-3 border-t">
          <div className="text-sm text-gray-700">
            Página {pagination.page} de {pagination.totalPages}
          </div>
          <div className="flex gap-2 items-center">
            <button
              onClick={() => handlePageChange(1)}
              disabled={pagination.page === 1}
              className="px-3 py-1 text-sm rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Primeira
            </button>

            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={20} />
            </button>

            {/* Page numbers with smart display */}
            {(() => {
              const pages = [];
              const current = pagination.page;
              const total = pagination.totalPages;

              // Always show first page
              if (current > 3) {
                pages.push(
                  <button
                    key={1}
                    onClick={() => handlePageChange(1)}
                    className="px-3 py-1 rounded hover:bg-gray-100"
                  >
                    1
                  </button>
                );
                if (current > 4) {
                  pages.push(<span key="dots1" className="px-2">...</span>);
                }
              }

              // Show pages around current
              const start = Math.max(1, current - 2);
              const end = Math.min(total, current + 2);

              for (let i = start; i <= end; i++) {
                pages.push(
                  <button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    className={`px-3 py-1 rounded ${
                      i === current
                        ? 'bg-blue-500 text-white'
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    {i}
                  </button>
                );
              }

              // Always show last page
              if (current < total - 2) {
                if (current < total - 3) {
                  pages.push(<span key="dots2" className="px-2">...</span>);
                }
                pages.push(
                  <button
                    key={total}
                    onClick={() => handlePageChange(total)}
                    className="px-3 py-1 rounded hover:bg-gray-100"
                  >
                    {total}
                  </button>
                );
              }

              return pages;
            })()}

            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
              className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight size={20} />
            </button>

            <button
              onClick={() => handlePageChange(pagination.totalPages)}
              disabled={pagination.page === pagination.totalPages}
              className="px-3 py-1 text-sm rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Última
            </button>

            {/* Jump to page */}
            <div className="flex items-center ml-4 border-l pl-4">
              <span className="text-sm text-gray-600 mr-2">Ir para:</span>
              <input
                type="number"
                min={1}
                max={pagination.totalPages}
                className="w-16 px-2 py-1 text-sm border rounded"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const page = parseInt((e.target as HTMLInputElement).value);
                    if (page >= 1 && page <= pagination.totalPages) {
                      handlePageChange(page);
                    }
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}